import { ReadonlyRadioGroup } from '@exam-center/components';
import {
  Box,
  CancelOutlinedIcon,
  CheckCircleOutlineRoundedIcon,
  Flex,
  Grid,
  SectionTitle,
  Text,
  ReadonlyField,
} from '@shared/components';

import { CERTIFICATE_TEMPLATE_NAME_MAP } from '../../constants';
import { type TemplateValue } from '../../utils/certificate-templates';

import HighlightCard from './highlight-card';

export interface CertificateSettingsReadonlyFieldsProps {
  visibleFields: {
    condition: boolean;
    averageScoreCondition: boolean;
  };
  values: {
    shouldIssue: boolean;
    averageScoreCondition?: 'all-centers' | 'each-center';
    delayedDays: number | null;
    validityDurationYears: number | null;
    passCertificate: {
      template?: TemplateValue;
      preview: JSX.Element;
    };
    failCertificate: {
      template?: TemplateValue;
      preview: JSX.Element;
    };
  };
}

export default function CertificateSettingsReadonlyFields(
  props: Readonly<CertificateSettingsReadonlyFieldsProps>,
) {
  const { visibleFields, values } = props;

  return (
    <>
      <SectionTitle title='Certificate Settings' />
      <Grid sx={{ gap: (theme) => theme.spacing(6), gridTemplate: '1fr / 240px 1fr' }}>
        <Box>
          <Text variant='body1Semibold'>ออกใบรับรองหรือไม่</Text>
        </Box>
        <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(4) }}>
          <ReadonlyRadioGroup
            options={[
              {
                label: 'ไม่ออกใบรับรอง',
                checked: !values.shouldIssue,
              },
              {
                label: 'ออกใบรับรอง',
                checked: values.shouldIssue,
              },
            ]}
          />
          {visibleFields?.condition ? (
            <Flex
              sx={{
                pl: (theme) => theme.spacing(8),
                flexDirection: 'column',
                gap: (theme) => theme.spacing(4),
              }}
            >
              <HighlightCard>
                <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(4) }}>
                  <Flex sx={{ alignItems: 'center', gap: (theme) => theme.spacing(2) }}>
                    <CheckCircleOutlineRoundedIcon
                      sx={{ color: (theme) => theme.palette.success.main }}
                    />
                    <Text variant='body1Semibold'>Template - Pass</Text>
                  </Flex>
                  <ReadonlyField
                    label='เลือก Certificate Template'
                    content={
                      values.passCertificate.template
                        ? CERTIFICATE_TEMPLATE_NAME_MAP[values.passCertificate.template]
                        : ''
                    }
                  />
                </Flex>

                {values.passCertificate.preview}
              </HighlightCard>
              <HighlightCard>
                <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(4) }}>
                  <Flex sx={{ alignItems: 'center', gap: (theme) => theme.spacing(2) }}>
                    <CancelOutlinedIcon sx={{ color: (theme) => theme.palette.error.main }} />
                    <Text variant='body1Semibold'>Template - Fail</Text>
                  </Flex>
                  <ReadonlyField
                    label='เลือก Certificate Template'
                    content={
                      values.failCertificate.template
                        ? CERTIFICATE_TEMPLATE_NAME_MAP[values.failCertificate.template]
                        : ''
                    }
                  />
                </Flex>
                {values.failCertificate.preview}
              </HighlightCard>
              <Flex sx={{ gap: (theme) => theme.spacing(4), flexDirection: 'column' }}>
                <ReadonlyRadioGroup
                  label='ระยะเวลาที่ส่ง Certificate'
                  required
                  row
                  options={[
                    {
                      label: 'ส่งทันที',
                      checked: !values.delayedDays,
                    },
                    {
                      checked: !!values.delayedDays,
                      label: (
                        <Text
                          sx={(theme) => ({
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing(2),
                            color: theme.palette.text.primary,
                          })}
                        >
                          <Text component='span' sx={{ flexShrink: 0 }} variant='inherit'>
                            ส่งหลัง
                          </Text>
                          <Text
                            component='span'
                            variant='inherit'
                            sx={(theme) => ({
                              flexShrink: 0,
                              width: '80px',
                              textAlign: 'center',
                              p: theme.spacing(2, 3),
                              bgcolor: theme.palette.grey[100],
                              borderRadius: theme.shape.rounded(1.5),
                              color: theme.palette.text.primary,
                            })}
                          >
                            {values.delayedDays ? values.delayedDays : ' '}
                          </Text>
                          <Text component='span' variant='inherit' sx={{ flexShrink: 0 }}>
                            วัน
                          </Text>
                        </Text>
                      ),
                    },
                  ]}
                />
                {visibleFields?.averageScoreCondition ? (
                  <ReadonlyRadioGroup
                    label='เงื่อนไขการคำนวณคะแนนเฉลี่ย'
                    required
                    options={[
                      {
                        label: 'คำนวณรวมทุกศูนย์สอบ',
                        checked: values.averageScoreCondition === 'all-centers',
                      },
                      {
                        label: 'คำนวณแต่ละศูนย์สอบ',
                        checked: values.averageScoreCondition === 'each-center',
                      },
                    ]}
                  />
                ) : null}
              </Flex>
            </Flex>
          ) : null}
        </Flex>
      </Grid>
      {visibleFields?.condition ? (
        <Grid sx={{ gap: (theme) => theme.spacing(6), gridTemplate: '1fr / 240px 1fr' }}>
          <Box>
            <Text variant='body1Semibold'>อายุของใบรับรอง</Text>
          </Box>
          <ReadonlyRadioGroup
            options={[
              {
                label: 'ไม่มีวันหมดอายุ',
                checked: values.validityDurationYears === null,
              },
              {
                label: (
                  <Text
                    sx={(theme) => ({
                      display: 'flex',
                      alignItems: 'center',
                      gap: theme.spacing(2),
                      color: theme.palette.text.primary,
                    })}
                  >
                    <Text component='span' sx={{ flexShrink: 0 }} variant='inherit'>
                      หมดอายุหลังจาก
                    </Text>
                    <Text
                      component='span'
                      variant='inherit'
                      sx={(theme) => ({
                        flexShrink: 0,
                        width: '80px',
                        textAlign: 'center',
                        p: theme.spacing(2, 3),
                        bgcolor: theme.palette.grey[100],
                        borderRadius: theme.shape.rounded(1.5),
                        color: theme.palette.text.primary,
                      })}
                    >
                      {values.validityDurationYears ? values.validityDurationYears : ' '}
                    </Text>
                    <Text component='span' variant='inherit' sx={{ flexShrink: 0 }}>
                      ปี (นับจากวันที่สอบผ่าน)
                    </Text>
                  </Text>
                ),
                checked: values.validityDurationYears !== null,
              },
            ]}
          />
        </Grid>
      ) : null}
    </>
  );
}
